<template>
  <v-app>
    <div class="login-container">
      <div class="form">
        <div class="mb-10 logo-container">
          <v-img v-if="setup" :src="setup.picture" max-width="260px" />
        </div>
        <v-stepper v-model="steps.currentStep" width="100%" max-width="510">
          <v-stepper-items style="width: 100%">
            <v-stepper-content :step="steps.config.email.step">
              <v-form method="POST" @submit.prevent="goCheckUser">
                <div class="text-center">
                  <h2 class="font-weight-medium mb-6">
                    {{ $vuetify.lang.t('$vuetify.pages.login_page.login_to_your_account') }}
                  </h2>

                  <v-text-field
                    id="login_email"
                    v-model.trim="credential.email.value"
                    class="border-radius"
                    outlined
                    autocomplete="username"
                    :disabled="loading.checkingEmail"
                    :error-messages="credential.email.errors"
                    prepend-inner-icon="mail"
                    label="Email"
                    :placeholder="$vuetify.lang.t('$vuetify.pages.login_page.type_your_email')"
                    type="text"
                    @keydown.space.prevent
                    @input="credential.email.errors = []"
                    @keyup.enter="goCheckUser"
                  />

                  <v-btn
                    v-if="!circularLoading"
                    id="login_button_continue"
                    color="primary"
                    x-large
                    class="border-radius"
                    block
                    type="submit"
                  >
                    {{ $vuetify.lang.t('$vuetify.pages.login_page.continue') }}
                  </v-btn>
                </div>
              </v-form>

              <div v-if="!circularLoading && isAllowedToRegister()" class="max-width text-center mt-14">
                <p class="font-weight-medium mt-4">
                  {{ $vuetify.lang.t('$vuetify.pages.login_page.dont_have_account') }}
                </p>

                <v-btn
                  id="login_button_continue"
                  outlined
                  x-large
                  block
                  class="border-radius"
                  @click="dialogPersonSign = true"
                >
                  {{ $vuetify.lang.t('$vuetify.btn.register') }}
                </v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-content :step="steps.config.password.step">
              <div class="text-center">
                <h3 class="font-weight-medium mb-6">
                  {{ $vuetify.lang.t('$vuetify.pages.login_page.login_to_your_account') }}
                </h3>

                <v-text-field
                  v-model="credential.email.value"
                  class="border-radius"
                  outlined
                  disabled
                  autocomplete="username"
                  prepend-inner-icon="mail"
                  label="Email"
                  placeholder=" "
                  type="text"
                />
                <form method="POST" @submit.prevent="goCheckPassword">
                  <input type="text" hidden autocomplete="username" :value="credential.email.value" />

                  <v-text-field
                    id="login_password"
                    v-model.trim="credential.password.value"
                    outlined
                    autocomplete="current-password"
                    class="border-radius"
                    prepend-inner-icon="vpn_key"
                    :append-icon="credential.password.type === 'password' ? 'visibility' : 'visibility_off'"
                    :disabled="loading.checkingPassword"
                    :error-messages="credential.password.errors"
                    :label="$vuetify.lang.t('$vuetify.pages.login_page.password')"
                    required
                    :placeholder="$vuetify.lang.t('$vuetify.pages.login_page.type_your_password')"
                    :type="credential.password.type"
                    @click:append="
                      credential.password.type =
                        credential.password.type === 'password'
                          ? (credential.password.type = 'text')
                          : (credential.password.type = 'password')
                    "
                    @input="credential.password.errors = []"
                    @keydown.space.prevent
                    @keyup.enter="credential?.password?.value?.length > 5 ? goCheckPassword() : false"
                  />

                  <template v-if="!circularLoading">
                    <div class="text-right mb-14">
                      <a id="login_button_forgot" @click="goToForgotMail">
                        {{ $vuetify.lang.t('$vuetify.pages.login_page.forgot_password') }}
                      </a>
                    </div>
                    <v-btn
                      id="login_go_button"
                      color="primary"
                      x-large
                      :disabled="
                        loading.checkingPassword ||
                        !credential.password.value ||
                        credential?.password?.value?.length < 5
                      "
                      class="border-radius"
                      block
                      type="submit"
                    >
                      {{ $vuetify.lang.t('$vuetify.pages.login_page.log_in') }}
                    </v-btn>

                    <v-btn
                      id="login_back_button"
                      x-large
                      outlined
                      class="border-radius border-none mt-6"
                      block
                      @click="backToEmail"
                    >
                      {{ $vuetify.lang.t('$vuetify.btn.back') }}
                    </v-btn>
                  </template>
                </form>
              </div>
            </v-stepper-content>

            <v-stepper-content :step="steps.config.newPassword.step">
              <v-form method="POST" @submit.prevent="handleChangePassword">
                <input type="text" hidden autocomplete="username" :value="credential.email.value" />

                <div class="mt-10">
                  <v-text-field
                    id="login_newPassword"
                    v-model.trim="credential.newPassword.value"
                    autocomplete="new-password"
                    :disabled="loading.changingPassword"
                    :append-icon="credential.newPassword.type === 'password' ? 'visibility' : 'visibility_off'"
                    outlined
                    :error-messages="newPasswordErrors"
                    class="mb-3 border-radius"
                    prepend-inner-icon="vpn_key"
                    :label="$vuetify.lang.t('$vuetify.pages.login_page.new_password')"
                    :type="credential.newPassword.type"
                    @input="$v.credential.newPassword.value.$touch()"
                    @keydown.space.prevent
                    @click:append="
                      credential.newPassword.type =
                        credential.newPassword.type === 'password'
                          ? (credential.newPassword.type = 'text')
                          : (credential.newPassword.type = 'password')
                    "
                  />
                  <v-text-field
                    id="login_confirm_newPassword"
                    v-model.trim="credential.confirmPassword.value"
                    autocomplete="new-password"
                    :disabled="loading.changingPassword"
                    :error-messages="confirmPasswordErrors"
                    :append-icon="credential.confirmPassword.type === 'password' ? 'visibility' : 'visibility_off'"
                    outlined
                    prepend-inner-icon="vpn_key"
                    :label="$vuetify.lang.t('$vuetify.pages.login_page.confirm')"
                    :type="credential.confirmPassword.type"
                    @input="$v.credential.confirmPassword.value.$touch()"
                    @keydown.space.prevent
                    @keyup.enter="handleChangePassword"
                    @click:append="
                      credential.confirmPassword.type =
                        credential.confirmPassword.type === 'password'
                          ? (credential.confirmPassword.type = 'text')
                          : (credential.confirmPassword.type = 'password')
                    "
                  />

                  <template v-if="!circularLoading">
                    <v-btn
                      id="login_newPassword_buttom_confirm"
                      color="primary"
                      x-large
                      :disabled="$v.$invalid"
                      class="border-radius"
                      block
                      type="submit"
                    >
                      {{ $vuetify.lang.t('$vuetify.btn.confirm') }}
                    </v-btn>

                    <v-btn
                      id="login_newPassword_buttom_cancel"
                      x-large
                      outlined
                      class="border-radius border-none mt-6"
                      block
                      @click="backToEmail"
                    >
                      {{ $vuetify.lang.t('$vuetify.btn.back') }}
                    </v-btn>
                  </template>
                </div>
              </v-form>
            </v-stepper-content>

            <v-stepper-content :step="steps.config.forgotPassword.step">
              <div class="text-center">
                <h3 class="font-weight-medium mb-6">
                  {{ $vuetify.lang.t('$vuetify.pages.login_page.forgot_your_password') }}
                </h3>
                <p>
                  {{ $vuetify.lang.t('$vuetify.pages.login_page.forgot_password_message') }}
                </p>
                <v-text-field
                  v-model="credential.email.value"
                  class="border-radius"
                  outlined
                  disabled
                  autocomplete="username"
                  prepend-inner-icon="mail"
                  label="Email"
                  placeholder=" "
                  type="text"
                />
                <v-btn
                  id="login_forgot_password_send_button"
                  color="primary"
                  x-large
                  class="border-radius"
                  block
                  :loading="forgotPasswordLoading"
                  @click="forgotPassword"
                >
                  {{ $vuetify.lang.t('$vuetify.pages.login_page.send_mail') }}
                </v-btn>
                <v-btn
                  id="login_forgot_password_back_button"
                  x-large
                  outlined
                  class="border-radius border-none mt-6"
                  block
                  :disabled="forgotPasswordLoading"
                  @click="backToEmail"
                >
                  {{ $vuetify.lang.t('$vuetify.btn.back') }}
                </v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-content :step="steps.config.acceptTerm.step">
              <dav-accept-term is-mobile />
            </v-stepper-content>

            <v-stepper-content :step="steps.config.notAcceptTerm.step">
              <div>
                {{ $vuetify.lang.t('$vuetify.pages.use_term.not_accept_term') }}
              </div>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  id="login_buttom_backToEmail_back"
                  x-large
                  class="border-radius"
                  color="secondary"
                  @click="backToEmail"
                >
                  {{ $vuetify.lang.t('$vuetify.btn.back') }}
                </v-btn>
              </v-card-actions>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
        <div v-if="circularLoading" class="text-center">
          <v-progress-circular :size="50" color="primary" indeterminate />
        </div>
      </div>

      <div class="background-image d-none d-lg-flex d-lg-flex">
        <img :src="getBackgroundImage()" alt="BackgroundImage" />
      </div>
    </div>

    <primeit-message-snackbar />
    <v-dialog v-model="dialogPersonSign" persistent width="600">
      <sign-up :init="dialogPersonSign" />
    </v-dialog>

    <v-dialog v-model="dialogForgotMail" hide-overlay persistent width="300">
      <div class="text-center">
        <v-card>
          <v-card-text>
            {{ msgForgotMail }}
          </v-card-text>
          <div class="text-center">
            <v-btn id="login_dialog_msgForgotMail_close" color="blue darken-1" text @click="dialogForgotMail = false">
              {{ $vuetify.lang.t('$vuetify.pages.login_page.close') }}
            </v-btn>
          </div>
        </v-card>
      </div>
    </v-dialog>
  </v-app>
</template>
<script>
import { CommonSecurity } from '@primeit/components-vue';
import { minLength, required, sameAs } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import SignUp from './sign-up.vue';
import moment from 'moment';

export default {
  name: 'SignIn',
  components: { SignUp },
  mixins: [validationMixin],
  validations: {
    credential: {
      newPassword: {
        value: {
          required,
          minLength: minLength(8),
          valid: function (value) {
            return /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{6,}$/.test(value);
          },
        },
      },
      confirmPassword: {
        value: {
          same: sameAs(function () {
            return this.credential.newPassword.value;
          }),
        },
      },
    },
  },
  data: () => ({
    showToRegister: true,
    dialogPersonSign: false,
    circularLoading: false,
    credential: {
      confirmPassword: {
        errors: [],
        type: 'password',
        value: null,
      },
      email: {
        errors: [],
        value: null,
      },
      newPassword: {
        errors: [],
        type: 'password',
        value: null,
      },
      password: {
        errors: [],
        type: 'password',
        value: null,
      },
    },
    loading: {
      changingPassword: false,
      checkingEmail: false,
      checkingPassword: false,
      checkinTermPending: false,
    },
    steps: {
      config: {
        acceptTerm: { complete: false, step: 4 },
        email: { complete: false, step: 1 },
        newPassword: { complete: false, step: 3 },
        forgotPassword: { complete: false, step: 6 },
        notAcceptTerm: { complete: false, step: 5 },
        password: { complete: false, step: 2 },
      },
      currentStep: undefined,
    },
    msgForgotMail: undefined,
    dialogForgotMail: false,
    forgotPasswordLoading: false,
  }),

  computed: {
    setup() {
      return this.$store.getters['credential/SETUP'];
    },
    feedback() {
      return this.$store.getters['common/FEEDBACK'];
    },
    checkEmail() {
      return this.$store.getters['credential/CHECK_EMAIL'];
    },
    checkPassword() {
      return this.$store.getters['credential/CHECK_PASSWORD'];
    },
    isMobile() {
      return this.$vuetify.breakpoint.width < 600;
    },
    resultTerm() {
      return this.$store.getters['credential/TERM_USER'];
    },
    resultForgot() {
      return this.$store.getters['credential/GET_FORGOT'];
    },
    newPasswordErrors() {
      const errors = [];
      if (this.$v.credential.newPassword.value.$dirty) {
        !this.$v.credential.newPassword.value.required &&
          errors.push(
            this.$vuetify.lang.t('$vuetify.message.isRequired', [
              this.$vuetify.lang.t('$vuetify.pages.login_page.password'),
            ])
          );

        !this.$v.credential.newPassword.value.minLength &&
          errors.push(this.$vuetify.lang.t('$vuetify.pages.login_page.password_min_length'));

        !this.$v.credential.newPassword.value.valid &&
          errors.push(this.$vuetify.lang.t('$vuetify.pages.login_page.valid_password'));
      }

      return errors;
    },
    confirmPasswordErrors() {
      const errors = [];
      if (this.$v.credential.confirmPassword.value.$dirty) {
        !this.$v.credential.confirmPassword.value.same &&
          errors.push(this.$vuetify.lang.t('$vuetify.pages.login_page.password_confirm'));
      }

      return errors;
    },
    changePassword() {
      return this.$store.getters['credential/CHANGE_PASSWORD'];
    },
    logoBackgroundColor() {
      return this.setup?.theme?.login?.light[0]?.value || 'white';
    },
  },

  watch: {
    checkEmail(val) {
      if (val?.result) {
        this.steps.currentStep = this.steps.config.password.step;
      } else {
        this.credential.email.errors = val.message ?? [
          this.$vuetify.lang.t('$vuetify.pages.login_page.user_not_found'),
        ];
      }

      this.loading.checkingEmail = false;
    },
    checkPassword(val) {
      if (val) {
        this.loading.checkingPassword = false;
        switch (val.result) {
          case 'NEW_PASSWORD_REQUIRED':
            this.steps.currentStep = this.steps.config.newPassword.step;
            break;
          case 'OK':
            this.checkAcceptTerm();
            break;
          case 'ERROR':
            this.credential.password.errors = [this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_password')];
            break;
        }
      }
    },
    loading: {
      handler(val) {
        this.circularLoading = Object.values(val).some(Boolean);
      },
      deep: true,
    },
    resultTerm(term) {
      this.loading.checkinTermPending = false;
      if (term && (term.length === 0 || term.every((opt) => opt === true))) {
        return this.goHome();
      }
      this.steps.currentStep = this.steps.config.acceptTerm.step;
      this.askLocation();
    },
    resultForgot(val) {
      if (val) {
        this.dialogForgotMail = true;
        this.forgotPasswordLoading = false;
        if (val.send) {
          this.msgForgotMail = this.$vuetify.lang.t('$vuetify.pages.login_page.message_forgot_mail');
          return;
        }

        this.msgForgotMail = this.$vuetify.lang.t('$vuetify.pages.login_page.message_forgot_mail_error');
      }
    },
    changePassword(val) {
      if (val) {
        this.loading.changingPassword = false;
        switch (val.result) {
          case 'OK':
            this.checkAcceptTerm();
            break;
          case 'ERROR':
            this.credential.newPassword.errors = [this.$vuetify.lang.t('$vuetify.pages.login_page.valid_password')];
            break;
        }
      }
    },
  },

  async created() {
    await CommonSecurity.signOut();
    this.listenEvents();
    this.handleRegistrationRoute();
  },

  mounted() {
    const seo = this.setup?.landing?.seo;
    if (seo) {
      document.head.querySelector('meta[name="description"]').content = seo.description;
      document.head.querySelector('meta[name="keywords"]').content = seo.meta_tags;
    }
    document.title = this.setup?.name;
  },

  methods: {
    getBackgroundImage() {
      if (this.setup?.background_login_consultorio) {
        return this.setup.background_login_consultorio;
      }

      return '/assets/backgroudLogin.jpg';
    },
    askLocation() {
      const options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      };

      window.navigator.geolocation.getCurrentPosition(this.setLocation, undefined, options);
    },
    setLocation(position) {
      if (position) {
        const coordinates = position.coords;
        if (coordinates) {
          this.location = {
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          };
        }
      }
    },
    currentStep(val) {
      this.showToRegister = true;

      if (val > 1) {
        this.showToRegister = false;
      }
    },
    listenEvents() {
      const vm = this;
      vm.$root.$on('show-dialog', function () {
        vm.showDialog();
      });

      vm.$root.$on('person-sign-close', function (val) {
        vm.dialogPersonSign = !val;
      });
    },
    isAllowedToRegister() {
      return this.setup?.settings?.allow_patient_to_register;
    },
    handleRegistrationRoute() {
      const ROUTE_HASH_PARAM_FOR_REGISTRATION = '#cadastro';
      if (this.isAllowedToRegister() && this.$route?.hash && this.$route.hash === ROUTE_HASH_PARAM_FOR_REGISTRATION) {
        this.dialogPersonSign = true;
      }
    },
    goCheckUser() {
      if (!this.credential.email.value || this.credential.email.value.length === 0) {
        this.credential.email.errors = [this.$vuetify.lang.t('$vuetify.pages.login_page.empty_mail')];
        return;
      }

      this.loading.checkingEmail = true;
      this.$store.dispatch('credential/CHECK_EMAIL', {
        email: this.credential.email.value,
      });
    },
    goCheckPassword() {
      if (!this.credential.password.value && this.credential.password.value.length < 5) {
        return;
      }

      this.loading.checkingPassword = true;
      this.$store.dispatch('credential/CHECK_PASSWORD', {
        password: this.credential.password.value,
      });
      sessionStorage.setItem('ENTRY_TIME', moment().toISOString());
    },
    goHome() {
      this.$router.replace({ path: '/' });
    },
    checkAcceptTerm() {
      this.loading.checkinTermPending = true;
      this.$store.dispatch('credential/FETCH_TERM_USER');
    },
    backToEmail() {
      this.steps.currentStep = this.steps.config.email.step;
    },
    goToForgotMail() {
      this.steps.currentStep = this.steps.config.forgotPassword.step;
    },
    forgotPassword() {
      this.forgotPasswordLoading = true;
      this.$store.dispatch('credential/FETCH_FORGOT', this.credential.email.value);
    },
    handleChangePassword() {
      if (!this.$v.$invalid) {
        this.loading.changingPassword = true;
        this.$store.dispatch('credential/CHANGE_PASSWORD', {
          newPassword: this.credential.newPassword.value,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.theme--light.v-application {
  background: #ffffff;
}

.v-sheet.v-stepper:not(.v-sheet--outlined) {
  box-shadow: none;
}

.login-container {
  display: flex;
  height: 100vh;
  width: 100vw;
}

.form {
  display: flex;
  flex-direction: column;
  width: 40%;
  align-items: center;
}

.logo-container {
  width: 100%;
  height: 30%;
  align-items: center;
  display: flex;
  justify-content: center;
  background-color: v-bind('logoBackgroundColor');
}

@media screen and (max-width: 1264px) {
  .form {
    width: 100%;
  }
}

.background-image {
  width: 100%;
  height: 100%;
  max-width: 70%;
}

.v-btn {
  text-transform: unset;
}

.border-radius {
  border-radius: 8px;
}

.border-none {
  border: none;
}
</style>
